import Cookies from 'js-cookie';
import debounce from 'lodash-es/debounce';

import Base from './base';
import Dropdown from './dropdown';
import './icon';

const CLASS_HIDDEN = 'is-hidden';
const CLASS_VISIBLE = 'responsive-is-visible';
const STATE_ATTRIBUTE = 'data-responsive-prio';
const LABEL_COLUMNS_DEFAULT = 'columns';

const dropdownButton = (labelColumns = LABEL_COLUMNS_DEFAULT) => `
	<button id="dropdown-button" class="button button--ghost button--icon responsive-table__settings" aria-controls="dropdown-menu" aria-haspopup="true" aria-owns="dropdown-menu">
		<span class="button__icon">
			<x-icon icon="gear"></x-icon>
		</span>

		<span class="button__label">
			<span data-ref="column-count">-</span> ${labelColumns}
		</span>
	</button>
`;

const dropdownList = (listItemsHTML = '') => `
	<ul id="dropdown-menu" role="menu" class="dropdown__list dropdown__list--row" aria-labelledby="dropdown-button">${listItemsHTML}</ul>
`;

const dropdownListItem = (id, label, checked = true) => `
	<li class="dropdown__item" role="none">
		<label for="${id}" class="option-group__item" role="menuitem">
			<input id="${id}" class="option-group__option" data-header-id="${id}" value="${id}" type="checkbox" ${checked ? 'checked' : ''}>
			${label}
		</label>
	</li>
`;

class ResponsiveTable extends Base {
	constructor(el) {
		super(el);

		// all headers
		this.headers = this.queryAll('th', this.refs.table);

		this.columns = this.getColumnsState();

		// create dropdown
		this.dropdown = this.createDropdown();

		// within in the dropdown is the element indicating how many columns are shown
		this.columnsShowIndicator = this.query('[data-ref="column-count"]', this.dropdownContainer);

		// then get toggles (checkboxes) from dropdown
		this.toggles = this.dropdown && this.dropdown.menu
			? Array.from(this.dropdown.menu.querySelectorAll('input[type=checkbox]'))
			: [];

		this.onResize = debounce(this.onResize, 100);

		this.columnCount = this.headers.filter(h => !h.hasAttribute('data-responsive-ignore')).length;
		this.responsiveColumnCount = this.columns.length;

		if (this.dropdown && this.dropdown.menu) {
			this.dropdown.menu.addEventListener('change', e => this.onChange(e));
		} else {
			console.warn('No dropdown or dropdown menu', this.dropdown);
		}

		this.on('resize', window);
	}

	// used for getting and saving settings
	get settingsKey() {
		const name = this.options.name || window.location.pathname;

		return `table-${name}`.toLowerCase().replace(' ', '-');
	}

	start() {
		this.updateToggles();
		this.updateCount();
		this.setTableOverflowState();

		this.el.classList.remove('is-loading');
	}

	onChange(e) {
		const { target, target: { type } } = e;

		if (type !== 'checkbox') {
			return;
		}

		this.toggleColumn(target);
		this.saveState(this.toggles);
		this.updateCount();
		this.setTableOverflowState();
	}

	onResize() {
		this.updateToggles();
		this.updateCount();
		this.setTableOverflowState();
	}

	getColumnsState() {
		const savedState = JSON.parse(Cookies.get(this.settingsKey) || '[]');
		const rows = this.queryAll('tr', this.refs.table);

		return this.headers.reduce((cols, el, columnIndex) => {
			const priority = el.getAttribute(STATE_ATTRIBUTE);

			if (!priority) {
				return cols;
			}

			const id = `header-${columnIndex}`;
			const headerState = savedState.find(s => s.id === id);
			const savedStateClass = headerState ? ResponsiveTable.getStateClass(headerState) : null;
			const label = el.textContent;

			const isVisible = () => el.offsetParent !== null;

			// array index (`headers.reduce`): zero based
			// `nth-child` selector: one based
			const cellSelectors = [
				`th:nth-child(${columnIndex + 1})`,
				`td:nth-child(${columnIndex + 1})`,
			].join(', ');

			const cells = rows.map(row => this.query(cellSelectors, row));

			cells.forEach((cell) => {
				cell.setAttribute('data-header-id', id);
				cell.setAttribute(STATE_ATTRIBUTE, priority);

				if (savedStateClass) {
					cell.classList.add(savedStateClass);
				}
			});

			cols.push({
				header: { id, el, label, isVisible },
				cells,
			});

			return cols;
		}, []);
	}


	createDropdown() {
		this.dropdownContainer = this.query('.js-dropdown-container');

		if (!this.dropdownContainer || !this.columns.length) {
			return false;
		}

		const listItems = this.columns.reduce((memo, column) => {
			const { header: { id, isVisible, label } } = column;

			return `${memo}${dropdownListItem(id, label, isVisible())}`;
		}, '');

		const { options: {
			labelColumns: columns,
		} } = this;

		this.dropdownContainer.innerHTML = `
			${dropdownButton(columns)}
			${dropdownList(listItems)}
		`;

		const dropdown = new Dropdown(this.dropdownContainer);

		dropdown.start();

		return dropdown;
	}

	toggleColumn(checkbox) {
		const { checked, value } = checkbox;
		const column = this.columns.find(c => c.header.id === value);

		column.cells.forEach(cell => ResponsiveTable.updateCellState(cell, checked));
	}

	saveState(checkboxes) {
		const togglesState = checkboxes.map(cb => ({
			id: cb.dataset.headerId,
			visible: cb.checked,
		}));

		Cookies.set(this.settingsKey, JSON.stringify(togglesState));
	}

	updateToggles() {
		this.toggles.forEach((toggle) => {
			toggle.checked = this.columns.some(c => c.header.id === toggle.value && c.header.isVisible());
		});
	}

	updateCount() {
		if (!this.columnsShowIndicator) {
			return;
		}

		const diff = this.columnCount - this.responsiveColumnCount;
		const responsiveShown = this.toggles.filter(t => t.checked).length;

		this.columnsShowIndicator.textContent = `${responsiveShown + diff}/${this.columnCount}`;
	}

	setTableOverflowState() {
		const { tableContainer } = this.refs;

		if (!tableContainer) {
			return;
		}

		tableContainer.classList.toggle('has-overflow', tableContainer.scrollWidth > tableContainer.clientWidth);
	}

	static getStateClass(state) {
		if (state.visible) {
			return CLASS_VISIBLE;
		}

		return CLASS_HIDDEN;
	}

	static updateCellState(cell, isVisible) {
		cell.classList.toggle(CLASS_VISIBLE, isVisible);
		cell.classList.toggle(CLASS_HIDDEN, !isVisible);
	}
}

export default ResponsiveTable;
